/**
 * AssetsLens — asset pack generation history with thumbnails, quick previews,
 * and a step-by-step generation wizard modal.
 *
 * Layout:
 *  - Top: "New Generation" wizard trigger
 *  - In-progress packs with live progress bars
 *  - Completed packs with thumbnail grids + Open in Canvas / Details CTAs
 *  - Asset detail modal (full image, metadata)
 *  - Generation wizard modal (3-step)
 *
 * Generation source: only the durable ad-generation runs surface here
 * (POST /api/v1/ad-generation/jobs → output_pack_id=pack_*). The legacy
 * playbook flow (pb_*) is intentionally hidden — its runs queued without
 * an executor and the resulting pack ids are not Creative Canvas-compatible.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Code2,
  Image as ImageIcon,
  LayoutTemplate,
  Layers,
  Link2,
  Loader2,
  Sparkles,
  Tv2,
  X,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';
import {
  adGenerationApi,
  adTemplatesApi,
  generationApi,
  ApiError,
  type AdPackStatusResponse,
  type BriefAngle,
  type FormatTemplateResponse,
  type GenerationBrief,
  type TemplateRecommendation,
  getApiBase,
} from '@/services/api';
import { useGenerationStore } from '@/stores/generation';
import { AssetDetailModal, type ModalAsset } from '@/components/AssetDetailModal';
import { LiveAdPackView } from '@/components/LiveAdPackView';

const AGENT_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || getApiBase();

/** Build the public Creative Canvas SPA URL for a pack. */
function canvasUrl(packId: string): string {
  return `${AGENT_BASE_URL.replace(/\/$/, '')}/canvas/${encodeURIComponent(packId)}`;
}

// ─── Thumbnail types + hook ─────────────────────────────────────────────────

// ThumbAsset extends ModalAsset so thumbnails can be passed directly to
// AssetDetailModal without an extra fetch.
type ThumbAsset = ModalAsset;

/**
 * Lazily fetches `final_ad` assets for a given packId from the Creative Canvas
 * API so we can show real thumbnail previews in the Assets lens.
 */
function useThumbnails(packId: string | null): {
  thumbnails: ThumbAsset[];
  loading: boolean;
  refresh: () => void;
} {
  const [thumbnails, setThumbnails] = useState<ThumbAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!packId) return;
    let cancelled = false;
    setLoading(true);
    setThumbnails([]);
    fetch(
      `${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/tracks/final_ad`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { assets?: ThumbAsset[] } | null) => {
        if (!cancelled && data?.assets) setThumbnails(data.assets);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [packId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return { thumbnails, loading, refresh };
}

/** Playbook + generation-run status strings normalized for UI buckets. */
function isActivePackStatus(status?: string): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return (
    s === 'running' ||
    s === 'pending' ||
    s === 'queued' ||
    s === 'initializing' ||
    s === 'generating' ||
    s === 'paused'
  );
}

function isCompletedPackStatus(status?: string): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return (
    s === 'completed' ||
    s === 'success' ||
    s === 'complete' ||
    s === 'published' ||
    s === 'partial'
  );
}

interface Props {
  brief: GenerationBrief;
  /** Pre-selected angle_id to prefill in the wizard */
  initialAngleId?: string;
  onWizardClose?: () => void;
  onMutated?: () => void;
}

export function AssetsLens({ brief, initialAngleId, onWizardClose, onMutated }: Props) {
  const navigate = useNavigate();
  const fetchBriefAdPackJobs = useGenerationStore((s) => s.fetchBriefAdPackJobs);
  const persistedAdJobs = useGenerationStore(
    (s) => s.briefAdPackJobsByBriefId[brief.brief_id] ?? [],
  );

  const [wizardOpen, setWizardOpen] = useState(!!initialAngleId);

  // Jobs launched directly from the wizard in this session, tracked locally.
  const [localJobs, setLocalJobs] = useState<AdPackStatusResponse[]>([]);

  // Initial fetch — without this the lens is empty until a job is launched.
  useEffect(() => {
    void fetchBriefAdPackJobs(brief.brief_id);
  }, [brief.brief_id, fetchBriefAdPackJobs]);

  const mergedAdJobs = useMemo(() => {
    const persistedIds = new Set(persistedAdJobs.map((j) => j.job_id));
    const extraLocal = localJobs.filter((j) => !persistedIds.has(j.job_id));
    const merged = [...extraLocal, ...persistedAdJobs];
    merged.sort((a, b) => {
      const ta = a.created_at ? Date.parse(String(a.created_at)) : 0;
      const tb = b.created_at ? Date.parse(String(b.created_at)) : 0;
      return tb - ta;
    });
    return merged;
  }, [localJobs, persistedAdJobs]);

  // Refresh durable ad pack jobs while any are in-flight.
  const persistedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const active = persistedAdJobs.filter((j) => isActivePackStatus(j.status));
    if (active.length > 0) {
      if (!persistedPollRef.current) {
        persistedPollRef.current = setInterval(() => {
          void fetchBriefAdPackJobs(brief.brief_id);
        }, 5000);
      }
    } else {
      if (persistedPollRef.current) {
        clearInterval(persistedPollRef.current);
        persistedPollRef.current = null;
      }
    }
    return () => {
      if (persistedPollRef.current) clearInterval(persistedPollRef.current);
    };
  }, [persistedAdJobs, fetchBriefAdPackJobs, brief.brief_id]);

  // Poll locally-launched jobs until they finish.
  const localPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const active = localJobs.filter((j) => isActivePackStatus(j.status));
    if (active.length > 0) {
      if (!localPollRef.current) {
        localPollRef.current = setInterval(async () => {
          const updates = await Promise.allSettled(
            active.map((j) => adGenerationApi.getJobStatus(j.job_id)),
          );
          setLocalJobs((prev) =>
            prev.map((j) => {
              const idx = active.findIndex((a) => a.job_id === j.job_id);
              if (idx === -1) return j;
              const result = updates[idx];
              return result.status === 'fulfilled' ? result.value : j;
            }),
          );
        }, 4000);
      }
    } else {
      if (localPollRef.current) {
        clearInterval(localPollRef.current);
        localPollRef.current = null;
      }
    }
    return () => {
      if (localPollRef.current) clearInterval(localPollRef.current);
    };
  }, [localJobs]);

  const handleArchiveJob = useCallback(
    async (job: AdPackStatusResponse) => {
      const runId = job.run_id;
      if (!runId) {
        // No durable run_id — just remove from local state
        setLocalJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
        return;
      }
      try {
        await generationApi.archiveRun(runId);
      } catch (e) {
        console.warn('[AssetsLens] archive failed', e);
      }
      // Optimistically remove from local state and refresh from server
      setLocalJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
      void fetchBriefAdPackJobs(brief.brief_id);
    },
    [brief.brief_id, fetchBriefAdPackJobs],
  );

  const handleJobLaunched = useCallback(
    (job: AdPackStatusResponse) => {
      setLocalJobs((prev) => [job, ...prev.filter((j) => j.job_id !== job.job_id)]);
      void fetchBriefAdPackJobs(brief.brief_id);
      onMutated?.();
      // Stay on the Assets tab — the inline LiveAdPackView renders live
      // progress here. Users can click through to the full streaming viewer
      // via the "View" button on any in-progress card.
    },
    [brief.brief_id, fetchBriefAdPackJobs, onMutated],
  );

  const adJobRunning = mergedAdJobs.filter((j) => isActivePackStatus(j.status));
  const adJobCompleted = mergedAdJobs.filter((j) => isCompletedPackStatus(j.status));

  const totalRunning = adJobRunning.length;
  const totalCompleted = adJobCompleted.length;

  const openWizard = () => setWizardOpen(true);
  const closeWizard = () => {
    setWizardOpen(false);
    onWizardClose?.();
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Asset Packs</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {totalCompleted === 0 && totalRunning === 0
              ? 'No generations yet'
              : `${totalCompleted} completed · ${totalRunning} in progress`}
          </p>
        </div>
        <button
          onClick={openWizard}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition shadow-sm"
        >
          <Zap size={14} /> Generate New Pack
        </button>
      </div>

      {/* In-progress packs */}
      {adJobRunning.length > 0 && (
        <section>
          <SectionHeader
            dot="bg-amber-400 animate-pulse"
            label="In Progress"
            count={totalRunning}
          />
          <div className="space-y-3">
            {adJobRunning.map((job) => (
              <div key={job.job_id} className="space-y-2">
                <LocalJobCard
                  job={job}
                  onCancel={() => {
                    // Archive the durable run so it stops appearing on refresh.
                    // This handles the common case where the backend lost the
                    // in-memory job (404 on cancel) but Firestore still shows it
                    // as "running" — archiving removes it from the active list.
                    void handleArchiveJob(job);
                  }}
                  onArchive={() => handleArchiveJob(job)}
                />
                {/* Live ad grid — relative wrapper lets us position the
                    "Live view" entry button at the bottom-right corner. */}
                <div className="relative">
                  <LiveAdPackView
                    jobId={job.job_id}
                    compact
                    onAdClick={(ad) => {
                      if (ad.canvas_asset_id) {
                        navigate(`/canvas/asset/${ad.canvas_asset_id}`);
                      } else if (ad.asset_url) {
                        window.open(ad.asset_url, '_blank', 'noopener');
                      }
                    }}
                    onComplete={() => {
                      // Firestore already knows the pack is done — immediately
                      // refresh the HTTP job list so the card above stops
                      // showing "RUNNING" without waiting for the next poll.
                      void fetchBriefAdPackJobs(brief.brief_id);
                      setLocalJobs((prev) =>
                        prev.map((j) =>
                          j.job_id === job.job_id ? { ...j, status: 'completed' } : j,
                        ),
                      );
                    }}
                  />
                  <button
                    onClick={() =>
                      navigate(
                        `/creative/live/${encodeURIComponent(job.job_id)}?brief_id=${encodeURIComponent(brief.brief_id)}`,
                      )
                    }
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-teal-700 bg-white/90 backdrop-blur-sm border border-teal-200 rounded-lg shadow-sm hover:bg-teal-50 hover:border-teal-300 transition"
                  >
                    <Tv2 size={11} />
                    Live view
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Completed packs */}
      {adJobCompleted.length > 0 && (
        <section>
          <SectionHeader dot="bg-emerald-400" label="Completed" count={totalCompleted} />
          <div className="space-y-3">
            {adJobCompleted.map((job) => (
              <CompletedPackCard
                key={job.job_id}
                job={job}
                onViewDetails={() => navigate(`/ad-generation/${job.job_id}`)}
                onArchive={() => handleArchiveJob(job)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {mergedAdJobs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Layers size={24} className="text-slate-300" />
          </div>
          <p className="text-sm font-semibold text-slate-600">No asset packs generated yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-5 max-w-sm">
            Generate your first asset pack to see ad images, videos, and creative reviews here.
          </p>
          <button
            onClick={openWizard}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition"
          >
            <Zap size={14} /> Generate First Pack
          </button>
        </div>
      )}

      {/* Generation wizard modal */}
      {wizardOpen && (
        <GenerationWizard
          brief={brief}
          initialAngleId={initialAngleId}
          onClose={closeWizard}
          onLaunch={(job) => {
            handleJobLaunched(job);
            closeWizard();
          }}
        />
      )}
    </div>
  );
}

/* ─── Thumbnail Grid + Hover Actions ────────────────────────────────────── */

function ThumbnailTile({
  thumb,
  onClick,
}: {
  thumb: ThumbAsset;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const url = thumb.thumbnail_url || thumb.media_url || null;
  const ces = thumb.scores?.['ces'] ?? null;

  return (
    <div
      className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {url ? (
        <img src={url} alt="Ad asset" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon size={18} className="text-slate-300" />
        </div>
      )}

      {ces !== null && !hovered && (
        <div className="absolute bottom-1.5 left-1.5 text-[9px] font-bold bg-black/50 text-white px-1.5 py-0.5 rounded-full backdrop-blur-sm">
          {Math.round(ces * 100)}
        </div>
      )}

      {/* Hover overlay — "view" indicator */}
      <div
        className={clsx(
          'absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-150',
          hovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <span className="text-[10px] font-semibold text-white bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full">
          View
        </span>
      </div>
    </div>
  );
}

/* ─── Asset Carousel ────────────────────────────────────────────────────────
 *  Collapsed (default): first 3 thumbnails in a compact 3-col grid
 *  Expanded: all thumbnails revealed with an animated slide-down
 *  Toggle only shown when there are more than 3 assets
 * ──────────────────────────────────────────────────────────────────────────── */

function AssetCarousel({
  thumbnails,
  loading,
  hasPackId,
  onThumbClick,
}: {
  thumbnails: ThumbAsset[];
  loading: boolean;
  hasPackId: boolean;
  onThumbClick: (thumb: ThumbAsset) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!hasPackId) return null;

  // ── Loading skeleton ──
  if (loading && thumbnails.length === 0) {
    return (
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="aspect-square bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // ── Empty / processing ──
  if (thumbnails.length === 0) {
    return (
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="aspect-square bg-slate-50 border border-dashed border-slate-200 rounded-xl flex items-center justify-center"
            >
              <ImageIcon size={14} className="text-slate-200" />
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-2">Thumbnails processing…</p>
      </div>
    );
  }

  const previewThumbs = thumbnails.slice(0, 3);
  const extraThumbs = thumbnails.slice(3);

  return (
    <div className="px-4 pb-2">
      {/* ── Always-visible first 3 ── */}
      <div className="grid grid-cols-3 gap-1.5">
        {previewThumbs.map((thumb) => (
          <ThumbnailTile key={thumb.asset_id} thumb={thumb} onClick={() => onThumbClick(thumb)} />
        ))}
      </div>

      {/* ── Overflow thumbnails (4+) — animated slide-in ── */}
      {extraThumbs.length > 0 && (
        <div
          className={clsx(
            'grid transition-all duration-300 ease-in-out',
            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="grid grid-cols-3 gap-1.5 pt-1.5">
              {extraThumbs.map((thumb) => (
                <ThumbnailTile
                  key={thumb.asset_id}
                  thumb={thumb}
                  onClick={() => onThumbClick(thumb)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Expand / collapse toggle ── */}
      {thumbnails.length > 3 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-center gap-1 mt-2 w-full py-1 text-[10px] font-semibold text-slate-400 hover:text-brand-teal transition-colors"
        >
          <span>{expanded ? 'Collapse' : `Show all ${thumbnails.length} ads`}</span>
          <ChevronDown
            size={11}
            className={clsx('transition-transform duration-300', expanded && 'rotate-180')}
          />
        </button>
      )}
    </div>
  );
}

/* ─── Section Header ────────────────────────────────────────────────────── */

function SectionHeader({
  dot,
  label,
  count,
}: {
  dot: string;
  label: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className={clsx('w-2 h-2 rounded-full', dot)} />
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
        {label}
      </span>
      <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">
        {count}
      </span>
    </div>
  );
}

/* ─── Local Job Card (directly-launched, uses AdPackStatusResponse) ─────── */

const PHASE_LABELS: Record<string, string> = {
  brief: 'Writing briefs',
  image: 'Generating images',
  review: 'Reviewing creatives',
  refine: 'Refining ads',
  package: 'Packaging',
  persona: 'Loading personas',
  concept: 'Building concepts',
};

function phaseLabel(phase?: string) {
  if (!phase) return 'Initialising…';
  return PHASE_LABELS[phase] ?? phase;
}

function LocalJobCard({
  job,
  onCancel,
  onArchive,
}: {
  job: AdPackStatusResponse;
  onCancel?: () => void;
  onArchive?: () => void;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const isActive = isActivePackStatus(job.status);
  const isDone = isCompletedPackStatus(job.status);
  const isFailed = job.status === 'failed' || job.status === 'cancelled';

  const handleCancel = async () => {
    if (!onCancel || cancelling) return;
    setCancelling(true);
    try {
      await adGenerationApi.cancelJob(job.job_id);
    } catch (e) {
      // 404 means the backend lost track of this job (server restarted).
      // The job is effectively dead — fall through and dismiss from UI.
      if (!(e instanceof ApiError && e.status === 404)) {
        console.warn('[AssetsLens] cancel failed', e);
        setCancelling(false);
        return;
      }
    }
    onCancel();
    setCancelling(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-teal-200 transition">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center border',
              isDone
                ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                : isFailed
                  ? 'bg-rose-50 border-rose-200 text-rose-500'
                  : 'bg-amber-50 border-amber-200 text-amber-600',
            )}
          >
            {isActive ? (
              <Loader2 size={14} className="animate-spin" />
            ) : isDone ? (
              <Check size={14} />
            ) : (
              <X size={14} />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-slate-400">{job.job_id.slice(0, 20)}…</span>
              <span
                className={clsx(
                  'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border',
                  isDone
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : isFailed
                      ? 'bg-rose-50 text-rose-600 border-rose-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200',
                )}
              >
                {job.status}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {isActive ? phaseLabel(job.pipeline_phase) : isDone ? `${job.ads_generated} ads generated` : job.error ?? 'Generation failed'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isActive && onCancel && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              title="Cancel this generation"
              className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-rose-500 disabled:opacity-40 transition"
            >
              {cancelling ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
              Cancel
            </button>
          )}
          {isFailed && onArchive && (
            <button
              onClick={async () => {
                setArchiving(true);
                await Promise.resolve(onArchive());
                setArchiving(false);
              }}
              disabled={archiving}
              title="Archive this run"
              className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600 disabled:opacity-40 transition"
            >
              {archiving ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
              Archive
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Completed Pack Card ───────────────────────────────────────────────────
 *  - Pack title → navigate to PackView (/creative/packs/:packId)
 *  - Thumbnails → AssetCarousel (hero + strip, expand to full grid)
 *  - "View" button (top-right) → PackView
 *  - Share / Embed icon buttons
 *  - "Generation Details" → subtle, bottom-right debug link
 * ─────────────────────────────────────────────────────────────────────────── */

function CompletedPackCard({
  job,
  onViewDetails,
  onArchive,
}: {
  job: AdPackStatusResponse;
  onViewDetails: () => void;
  onArchive?: () => void;
}) {
  const [archiving, setArchiving] = useState(false);
  const navigate = useNavigate();
  const packId = job.output_pack_id || job.pack_id || null;
  const summary = job.manifest?.summary ?? null;
  const rankedAds = job.manifest?.ranked_ads ?? [];

  const { thumbnails, loading: thumbLoading } = useThumbnails(packId);
  const [modalAsset, setModalAsset] = useState<ThumbAsset | null>(null);
  const [copyToast, setCopyToast] = useState<'link' | 'embed' | null>(null);

  const displayName = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const ad of rankedAds) {
      const p = ad.persona?.replace(/_/g, ' ');
      if (p && !seen.has(p)) { seen.add(p); names.push(p); }
    }
    if (names.length > 0) {
      const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
      return names.slice(0, 2).map(cap).join(' · ');
    }
    return packId ? packId.slice(0, 20) + '…' : 'Ad Pack';
  }, [rankedAds, packId]);

  const dateLabel = useMemo(() => {
    if (!job.created_at) return null;
    try {
      return new Date(job.created_at).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return null; }
  }, [job.created_at]);

  const handleCopyLink = useCallback(() => {
    if (!packId) return;
    void navigator.clipboard.writeText(canvasUrl(packId));
    setCopyToast('link');
    setTimeout(() => setCopyToast(null), 2000);
  }, [packId]);

  const handleCopyEmbed = useCallback(() => {
    if (!packId) return;
    const snippet = `<iframe src="${canvasUrl(packId)}" width="1200" height="800" style="border:none;border-radius:12px;" allowfullscreen></iframe>`;
    void navigator.clipboard.writeText(snippet);
    setCopyToast('embed');
    setTimeout(() => setCopyToast(null), 2000);
  }, [packId]);

  // Include jobId as query param so PackView can mint share tokens
  const packHomePath = packId
    ? `/creative/packs/${packId}${job.job_id ? `?job=${encodeURIComponent(job.job_id)}` : ''}`
    : null;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-slate-300 hover:shadow-sm transition-all">
        {/* ── Pack header ── */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            {/* Clickable title → PackView */}
            <button
              className="flex items-start gap-2 min-w-0 text-left group"
              onClick={() => packHomePath && navigate(packHomePath)}
              disabled={!packHomePath}
            >
              <div className="w-6 h-6 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                <Check size={11} className="text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h3 className={clsx(
                  'text-sm font-semibold text-slate-800 truncate',
                  packHomePath && 'group-hover:text-violet-700 transition-colors',
                )}>
                  {displayName}
                </h3>
                <div className="flex items-center gap-3 flex-wrap mt-0.5">
                  <StatChip
                    icon={<Sparkles size={9} />}
                    label={`${job.ads_generated ?? summary?.total_ads ?? 0} ads`}
                  />
                  {summary?.mean_ces != null && (
                    <StatChip
                      icon={<Layers size={9} />}
                      label={`CES ${Math.round((summary.mean_ces ?? 0) * 100)}`}
                    />
                  )}
                  {dateLabel && (
                    <span className="text-[10px] text-slate-400">{dateLabel}</span>
                  )}
                </div>
              </div>
              {packHomePath && (
                <ChevronRight size={13} className="text-slate-300 group-hover:text-violet-400 transition-colors mt-0.5 shrink-0" />
              )}
            </button>

            {/* Action buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Embed */}
              {packId && (
                <button
                  onClick={handleCopyEmbed}
                  title="Copy embed snippet"
                  className={clsx(
                    'w-7 h-7 flex items-center justify-center rounded-lg border text-xs transition',
                    copyToast === 'embed'
                      ? 'border-teal-300 bg-teal-50 text-teal-600'
                      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600',
                  )}
                >
                  {copyToast === 'embed' ? <Check size={12} /> : <Code2 size={12} />}
                </button>
              )}
              {/* Share */}
              {packId && (
                <button
                  onClick={handleCopyLink}
                  title="Copy Canvas link"
                  className={clsx(
                    'w-7 h-7 flex items-center justify-center rounded-lg border text-xs transition',
                    copyToast === 'link'
                      ? 'border-violet-300 bg-violet-50 text-violet-600'
                      : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600',
                  )}
                >
                  {copyToast === 'link' ? <Check size={12} /> : <Link2 size={12} />}
                </button>
              )}
              {/* View → PackView */}
              {packHomePath && (
                <button
                  onClick={() => navigate(packHomePath)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:border-slate-300 hover:text-slate-800 transition"
                >
                  View <ChevronRight size={11} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Asset carousel — hero + strip (collapsed) or full grid (expanded) ── */}
        <AssetCarousel
          thumbnails={thumbnails}
          loading={thumbLoading}
          hasPackId={!!packId}
          onThumbClick={setModalAsset}
        />

        {/* ── Footer: archive + generation details ── */}
        <div className="flex items-center justify-between px-4 pb-3 pt-2 border-t border-slate-50">
          {onArchive ? (
            <button
              onClick={async () => {
                setArchiving(true);
                await Promise.resolve(onArchive());
                setArchiving(false);
              }}
              disabled={archiving}
              title="Archive this pack"
              className="flex items-center gap-1 text-[10px] text-slate-300 hover:text-slate-500 disabled:opacity-40 transition"
            >
              {archiving ? <Loader2 size={10} className="animate-spin" /> : <Archive size={10} />}
              Archive
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onViewDetails}
            className="text-[10px] text-slate-300 hover:text-slate-400 transition underline-offset-2 hover:underline"
          >
            Generation Details
          </button>
        </div>
      </div>

      {/* Asset detail modal — opened by thumbnail click */}
      {modalAsset && packId && (
        <AssetDetailModal
          asset={modalAsset}
          packId={packId}
          onClose={() => setModalAsset(null)}
          onNavigateToTemplate={(templateId) => {
            setModalAsset(null);
            navigate(`/creative-templates/${templateId}`);
          }}
        />
      )}
    </>
  );
}

function StatChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
      {icon}
      {label}
    </span>
  );
}

/* ─── Generation Wizard Modal ───────────────────────────────────────────── */

type WizardStep = 'angle' | 'strategy' | 'format' | 'options' | 'confirm';

interface WizardState {
  selectedAngleId: string | null;
  creativeStrategies: string[];
  /** Template grounding mode. false = no exemplars (legacy); true = use exemplars. */
  useTemplateGrounding: boolean;
  /** When useTemplateGrounding=true and empty → AI auto-selects. When non-empty → pinned. */
  pinnedTemplateIds: string[];
  exemplarFraming: 'composition_match' | 'strict_template' | 'style_match';
  channels: string[];
  sizes: string[];
  totalAds: number;
  fastMode: boolean;
  enableRefinement: boolean;
  generateLandingPages: boolean;
}

const STRATEGY_OPTIONS = [
  {
    value: 'auto',
    label: 'Auto',
    desc: 'AI picks the best strategy',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <circle cx="32" cy="32" r="28" fill="#0D9488" fillOpacity=".12" />
        <circle cx="32" cy="32" r="18" fill="#0D9488" fillOpacity=".2" />
        <path d="M22 32h4l3-8 5 16 3-10 3 6h2" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="48" cy="20" r="4" fill="#7C3AED" fillOpacity=".8" />
        <circle cx="48" cy="20" r="2" fill="#7C3AED" />
      </svg>
    ),
  },
  {
    value: 'product_showcase',
    label: 'Product Showcase',
    desc: 'Hero product, clean features',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <rect x="16" y="12" width="32" height="40" rx="4" fill="#0D9488" fillOpacity=".12" stroke="#0D9488" strokeWidth="1.5" />
        <rect x="22" y="18" width="20" height="18" rx="2" fill="#0D9488" fillOpacity=".25" />
        <circle cx="32" cy="27" r="5" fill="#0D9488" fillOpacity=".6" />
        <rect x="22" y="40" width="20" height="3" rx="1.5" fill="#0D9488" fillOpacity=".4" />
        <rect x="24" y="45" width="16" height="2" rx="1" fill="#0D9488" fillOpacity=".25" />
        <path d="M28 27l2.5 2.5L36 24" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'lifestyle_action',
    label: 'Lifestyle Action',
    desc: 'Aspirational, in-the-moment',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <ellipse cx="32" cy="48" rx="20" ry="4" fill="#0D9488" fillOpacity=".12" />
        <circle cx="32" cy="20" r="7" fill="#7C3AED" fillOpacity=".2" stroke="#7C3AED" strokeWidth="1.5" />
        <path d="M26 32c0-3.3 2.7-6 6-6s6 2.7 6 6v8H26v-8z" fill="#0D9488" fillOpacity=".35" stroke="#0D9488" strokeWidth="1.5" />
        <path d="M26 38l-5 4M38 38l5 4" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" />
        <path d="M29 32l-3-4M35 32l3-4" stroke="#0D9488" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M40 16c3 1 5 4 5 7" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2" />
      </svg>
    ),
  },
  {
    value: 'problem_solution',
    label: 'Problem → Solution',
    desc: 'Pain point to outcome narrative',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <rect x="8" y="16" width="20" height="32" rx="3" fill="#EF4444" fillOpacity=".12" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="3 2" />
        <path d="M18 28l-4 4 4 4" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 28l4 4-4 4" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 32h4" stroke="#0D9488" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M33 30l2 2-2 2" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="36" y="16" width="20" height="32" rx="3" fill="#0D9488" fillOpacity=".12" stroke="#0D9488" strokeWidth="1.5" />
        <path d="M43 32l2.5 2.5L51 27" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'social_proof',
    label: 'Social Proof',
    desc: 'Reviews, testimonials, trust signals',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <rect x="12" y="16" width="40" height="28" rx="4" fill="#7C3AED" fillOpacity=".1" stroke="#7C3AED" strokeWidth="1.5" />
        <path d="M20 24c-2 0-3 1-3 3v4c0 2 1 3 3 3h1l2 4 2-4h8c2 0 3-1 3-3v-4c0-2-1-3-3-3H20z" fill="#7C3AED" fillOpacity=".25" />
        <path d="M20 30h16M20 26h10" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M38 20l1.5 3 3 .5-2 2 .5 3-3-1.5-3 1.5.5-3-2-2 3-.5z" fill="#F59E0B" />
        <circle cx="44" cy="42" r="6" fill="#0D9488" fillOpacity=".15" stroke="#0D9488" strokeWidth="1.5" />
        <path d="M41 42l2 2 4-4" stroke="#0D9488" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'ugc',
    label: 'UGC',
    desc: 'Authentic, raw, creator-native',
    icon: (
      <svg viewBox="0 0 64 64" className="w-full h-full" fill="none">
        <rect x="16" y="12" width="32" height="40" rx="6" fill="#1E293B" fillOpacity=".08" stroke="#1E293B" strokeOpacity=".4" strokeWidth="1.5" />
        <rect x="20" y="16" width="24" height="30" rx="3" fill="#0D9488" fillOpacity=".12" />
        <circle cx="32" cy="31" r="6" fill="#0D9488" fillOpacity=".3" stroke="#0D9488" strokeWidth="1.5" />
        <circle cx="32" cy="31" r="2.5" fill="#0D9488" />
        <circle cx="40" cy="18" r="2" fill="#7C3AED" fillOpacity=".6" />
        <rect x="22" y="48" width="8" height="2" rx="1" fill="#1E293B" fillOpacity=".2" />
      </svg>
    ),
  },
] as const;

const CHANNEL_OPTIONS = [
  { value: 'meta', label: 'Meta', sub: 'Facebook & Instagram' },
  { value: 'google', label: 'Google', sub: 'Display & YouTube' },
  { value: 'tiktok', label: 'TikTok', sub: 'Short-form video' },
  { value: 'programmatic', label: 'Programmatic', sub: 'DSP display' },
];

const SIZE_OPTIONS = [
  { value: '1080x1080', label: '1:1', sub: '1080×1080 · Feed square' },
  { value: '1080x1350', label: '4:5', sub: '1080×1350 · Feed portrait' },
  { value: '1080x1920', label: '9:16', sub: '1080×1920 · Stories/Reels' },
  { value: '1200x628', label: '1.9:1', sub: '1200×628 · Link ads' },
];

function GenerationWizard({
  brief,
  initialAngleId,
  onClose,
  onLaunch,
}: {
  brief: GenerationBrief;
  initialAngleId?: string;
  onClose: () => void;
  onLaunch: (job: AdPackStatusResponse) => void;
}) {
  const [step, setStep] = useState<WizardStep>(initialAngleId ? 'strategy' : 'angle');
  const [state, setState] = useState<WizardState>({
    selectedAngleId: initialAngleId ?? null,
    creativeStrategies: ['auto'],
    // Default ON — three exemplars per ad ground generation in proven
    // template compositions (matches the team's research finding that
    // 3 is "a strong default" — see AdPackRequest.exemplar_count docs).
    // Users can toggle off in the wizard for freeform generation.
    useTemplateGrounding: true,
    pinnedTemplateIds: [],
    exemplarFraming: 'composition_match',
    channels: ['meta'],
    sizes: ['1080x1350'],
    totalAds: 6,
    fastMode: false,
    enableRefinement: true,
    generateLandingPages: false,
  });
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const angles = (brief.selected_angles ?? []).filter((a) => a.status === 'approved');
  const selectedAngle = angles.find((a) => a.angle_id === state.selectedAngleId) ?? null;

  const STEPS: { key: WizardStep; label: string; number: number }[] = [
    { key: 'angle', label: 'Angle', number: 1 },
    { key: 'strategy', label: 'Strategy', number: 2 },
    { key: 'format', label: 'Format', number: 3 },
    { key: 'options', label: 'Options', number: 4 },
    { key: 'confirm', label: 'Launch', number: 5 },
  ];

  const handleLaunch = async () => {
    setError(null);
    setLaunching(true);
    try {
      const strategyValue =
        state.creativeStrategies.includes('auto') || state.creativeStrategies.length === 0
          ? 'auto'
          : state.creativeStrategies.join(',');

      const { job_id } = await adGenerationApi.submitJob({
        client_uid: brief.client_uid,
        brief_id: brief.brief_id,
        total_ads: state.totalAds,
        creative_strategy: strategyValue,
        channels: state.channels.length ? state.channels : undefined,
        sizes: state.sizes.length ? state.sizes : undefined,
        fast_mode: state.fastMode,
        enable_refinement: state.enableRefinement,
        generate_landing_pages: state.generateLandingPages,
        // Template grounding
        exemplar_count: state.useTemplateGrounding ? 3 : 0,
        exemplar_template_ids_override:
          state.useTemplateGrounding && state.pinnedTemplateIds.length
            ? state.pinnedTemplateIds
            : undefined,
        exemplar_framing: state.useTemplateGrounding ? state.exemplarFraming : undefined,
        // Live streaming — opt in so the AssetsLens hydrates per-ad cards
        // in real time as the pipeline emits state transitions. Decoupled
        // from the canonical pack bundle on the backend.
        live_view_enabled: true,
      });

      // Fetch initial status so we have something concrete to display
      let initialStatus: AdPackStatusResponse;
      try {
        initialStatus = await adGenerationApi.getJobStatus(job_id);
      } catch {
        // If status isn't ready yet, build a minimal placeholder
        initialStatus = {
          job_id,
          client_uid: brief.client_uid,
          status: 'pending',
          percent_complete: 0,
          phases_completed: [],
          phases_remaining: [],
          personas_count: 0,
          ads_generated: 0,
          ads_reviewed: 0,
        };
      }

      onLaunch(initialStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to launch generation');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-900">Generate Asset Pack</h2>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
            >
              <X size={14} />
            </button>
          </div>
          {/* Step progress */}
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1 flex-1">
                <div className="flex items-center gap-1.5">
                  <div
                    className={clsx(
                      'w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0',
                      step === s.key
                        ? 'bg-teal-500 text-white'
                        : STEPS.findIndex((x) => x.key === step) > i
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-400',
                    )}
                  >
                    {STEPS.findIndex((x) => x.key === step) > i ? (
                      <Check size={10} />
                    ) : (
                      s.number
                    )}
                  </div>
                  <span
                    className={clsx(
                      'text-[10px] font-semibold hidden sm:block',
                      step === s.key ? 'text-teal-600' : 'text-slate-400',
                    )}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px bg-slate-200 mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-5 min-h-[300px] max-h-[60vh] overflow-y-auto">
          {step === 'angle' && (
            <AngleStep
              angles={angles}
              selectedId={state.selectedAngleId}
              onSelect={(id) => setState((s) => ({ ...s, selectedAngleId: id }))}
            />
          )}
          {step === 'strategy' && (
            <StrategyStep
              selected={state.creativeStrategies}
              onChange={(v) => setState((s) => ({ ...s, creativeStrategies: v }))}
            />
          )}
          {step === 'format' && (
            <FormatStep
              state={state}
              onChange={(updates) => setState((s) => ({ ...s, ...updates }))}
              brief={brief}
              selectedAngle={selectedAngle}
            />
          )}
          {step === 'options' && (
            <OptionsStep
              state={state}
              onChange={(updates) => setState((s) => ({ ...s, ...updates }))}
              selectedAngle={selectedAngle}
            />
          )}
          {step === 'confirm' && (
            <ConfirmStep
              state={state}
              brief={brief}
              selectedAngle={selectedAngle}
            />
          )}
        </div>

        {error && (
          <div className="mx-6 mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Footer actions */}
        <div className="px-6 pb-5 flex items-center justify-between border-t border-slate-100 pt-4">
          <button
            onClick={step === 'angle' ? onClose : () => {
              const idx = STEPS.findIndex((s) => s.key === step);
              setStep(STEPS[idx - 1].key);
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 transition"
          >
            {step === 'angle' ? 'Cancel' : '← Back'}
          </button>

          {step !== 'confirm' ? (
            <button
              onClick={() => {
                const idx = STEPS.findIndex((s) => s.key === step);
                setStep(STEPS[idx + 1].key);
              }}
              disabled={step === 'angle' && !state.selectedAngleId && angles.length > 0}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-teal-500 rounded-lg hover:bg-teal-600 disabled:opacity-40 transition"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleLaunch}
              disabled={launching}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 transition"
            >
              {launching ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Zap size={13} />
              )}
              Launch Generation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Wizard Step: Angle ────────────────────────────────────────────────── */

function AngleStep({
  angles,
  selectedId,
  onSelect,
}: {
  angles: BriefAngle[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (angles.length === 0) {
    return (
      <div className="text-center py-8">
        <Sparkles size={24} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm font-medium text-slate-600">No approved angles</p>
        <p className="text-xs text-slate-400 mt-1">
          Approve at least one angle in the Angles tab before generating assets.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500 mb-3">
        Which approved angle should guide this asset pack?
      </p>
      {angles.map((a) => (
        <button
          key={a.angle_id}
          onClick={() => onSelect(a.angle_id)}
          className={clsx(
            'w-full flex items-start gap-3 p-3 rounded-xl border text-left transition',
            selectedId === a.angle_id
              ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-300'
              : 'border-slate-200 hover:border-slate-300 bg-white',
          )}
        >
          <div
            className={clsx(
              'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0',
              selectedId === a.angle_id ? 'border-teal-500 bg-teal-500' : 'border-slate-300',
            )}
          >
            {selectedId === a.angle_id && <Check size={9} className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{a.hook}</p>
            {a.scenario_name && (
              <p className="text-[10px] text-teal-600 mt-0.5">{a.scenario_name}</p>
            )}
            {a.message && (
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{a.message}</p>
            )}
          </div>
        </button>
      ))}
      <div className="pt-2 border-t border-slate-100">
        <button
          onClick={() => onSelect('__any__')}
          className={clsx(
            'w-full flex items-center gap-2 p-3 rounded-xl border text-left text-xs transition',
            selectedId === '__any__'
              ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-300 font-semibold text-teal-700'
              : 'border-dashed border-slate-300 text-slate-500 hover:border-slate-400',
          )}
        >
          <Layers size={12} />
          Generate across all approved angles
        </button>
      </div>
    </div>
  );
}

/* ─── Wizard Step: Strategy (Template) ─────────────────────────────────── */

function StrategyStep({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const isAuto = selected.length === 1 && selected[0] === 'auto';
  const isMulti = !isAuto && selected.length > 0;

  const toggle = (value: string) => {
    if (value === 'auto') {
      // Auto is exclusive — selecting it clears everything else
      onChange(['auto']);
      return;
    }
    // Deselect Auto if it was set, then toggle this value
    const without = selected.filter((v) => v !== 'auto');
    if (without.includes(value)) {
      const next = without.filter((v) => v !== value);
      onChange(next.length === 0 ? ['auto'] : next);
    } else {
      onChange([...without, value]);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-0.5">Creative Strategy</p>
        <p className="text-[11px] text-slate-500">
          Choose the narrative framework that guides how ads are constructed.
        </p>
      </div>

      {/* Context banner when multiple are selected */}
      {isMulti && (
        <div className="flex items-start gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
          <div className="w-3.5 h-3.5 rounded-full bg-teal-500 flex items-center justify-center shrink-0 mt-0.5">
            <Check size={8} className="text-white" />
          </div>
          <p className="text-[11px] text-teal-700 leading-snug">
            <span className="font-semibold">{selected.length} strategies selected.</span>{' '}
            The asset pack will generate ads in each selected style — great for A/B testing multiple narrative angles at once.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5">
        {STRATEGY_OPTIONS.map((opt) => {
          const isSelected = selected.includes(opt.value);
          const isAutoOpt = opt.value === 'auto';
          return (
            <button
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className={clsx(
                'flex flex-col items-start p-3 rounded-xl border text-left transition group',
                isSelected
                  ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-white ring-1 ring-teal-300'
                  : 'border-slate-200 hover:border-slate-300 bg-white',
              )}
            >
              <div className="w-14 h-14 mb-2.5 rounded-xl overflow-hidden bg-slate-50 border border-slate-100 p-1.5">
                {opt.icon}
              </div>
              <div className="flex items-start justify-between w-full gap-1">
                <p className={clsx(
                  'text-xs font-semibold leading-tight',
                  isSelected ? 'text-teal-700' : 'text-slate-700',
                )}>
                  {opt.label}
                </p>
                {/* Auto uses a radio dot; others use a checkbox */}
                <div
                  className={clsx(
                    'shrink-0 mt-0.5 flex items-center justify-center transition',
                    isAutoOpt
                      ? 'w-4 h-4 rounded-full border-2'
                      : 'w-4 h-4 rounded border-2',
                    isSelected
                      ? 'border-teal-500 bg-teal-500'
                      : 'border-slate-300 bg-white',
                  )}
                >
                  {isSelected && <Check size={8} className="text-white" />}
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{opt.desc}</p>
            </button>
          );
        })}
      </div>

      {isAuto && (
        <p className="text-[10px] text-slate-400 leading-relaxed">
          Or pick specific strategies below — you can select multiple to run them in parallel across the pack.
        </p>
      )}
    </div>
  );
}

/* ─── Wizard Step: Format ───────────────────────────────────────────────── */

function ToggleChip({
  label,
  sub,
  selected,
  onClick,
}: {
  label: string;
  sub?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex flex-col items-start px-3 py-2.5 rounded-xl border text-left transition',
        selected
          ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-300'
          : 'border-slate-200 hover:border-slate-300 bg-white',
      )}
    >
      <span className={clsx('text-xs font-semibold', selected ? 'text-teal-700' : 'text-slate-700')}>
        {label}
      </span>
      {sub && <span className="text-[10px] text-slate-400 mt-0.5">{sub}</span>}
    </button>
  );
}

/* ─── Template Picker ────────────────────────────────────────────────────── */

function TemplateThumbnail({ template, selected, onSelect }: {
  template: FormatTemplateResponse;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        'relative flex flex-col rounded-xl border-2 overflow-hidden text-left transition-all',
        selected
          ? 'border-teal-500 shadow-md shadow-teal-100'
          : 'border-slate-200 hover:border-slate-300',
      )}
    >
      {/* Image area */}
      <div className="w-full aspect-[4/3] bg-slate-100 overflow-hidden relative">
        {template.hero_ad_image_url ? (
          <img
            src={template.hero_ad_image_url}
            alt={template.template_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-slate-50 to-slate-100 p-3">
            <LayoutTemplate size={18} className="text-slate-300" />
            <span className="text-[9px] text-slate-400 text-center font-medium leading-tight line-clamp-2">
              {template.category_scope || template.funnel_stage || 'Template'}
            </span>
          </div>
        )}
        {selected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center shadow">
            <Check size={10} className="text-white" />
          </div>
        )}
      </div>
      {/* Info */}
      <div className="px-2 py-1.5">
        <p className="text-[11px] font-semibold text-slate-800 truncate leading-tight">
          {template.template_name}
        </p>
        <p className="text-[10px] text-slate-400 truncate leading-tight">
          {template.category_scope || template.funnel_stage || template.scope}
        </p>
      </div>
    </button>
  );
}

function TemplatePickerSection({
  state,
  onChange,
  brief,
  selectedAngle,
}: {
  state: WizardState;
  onChange: (u: Partial<WizardState>) => void;
  brief: GenerationBrief;
  selectedAngle: BriefAngle | null;
}) {
  const [recs, setRecs] = useState<TemplateRecommendation[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FormatTemplateResponse[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load recommendations when the section mounts or when key context changes.
  useEffect(() => {
    const strategy =
      state.creativeStrategies.includes('auto') || state.creativeStrategies.length === 0
        ? undefined
        : state.creativeStrategies[0];
    const channel = state.channels[0];

    setRecsLoading(true);
    adTemplatesApi
      .recommendTemplates({
        client_uid: brief.client_uid,
        creative_strategy: strategy,
        channel,
        persona_slug: (brief as { persona_id?: string }).persona_id ?? undefined,
        limit: 3,
      })
      .then((r) => setRecs(r.recommendations))
      .catch(() => setRecs([]))
      .finally(() => setRecsLoading(false));
  }, [brief.client_uid, state.creativeStrategies, state.channels]);  // eslint-disable-line

  // Debounced search for explore mode.
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      setSearchLoading(true);
      adTemplatesApi
        .listFormatTemplates({ search: q.trim(), sort_by: 'quality', limit: 20 })
        .then((r) => setSearchResults(r.templates))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 350);
  };

  const togglePin = (id: string) => {
    const next = state.pinnedTemplateIds.includes(id)
      ? state.pinnedTemplateIds.filter((x) => x !== id)
      : [...state.pinnedTemplateIds, id];
    onChange({
      useTemplateGrounding: true,
      pinnedTemplateIds: next,
    });
  };

  const selectAiMode = () => {
    onChange({ useTemplateGrounding: true, pinnedTemplateIds: [] });
  };

  const selectNone = () => {
    onChange({ useTemplateGrounding: false, pinnedTemplateIds: [] });
  };

  return (
    <div>
      <p className="text-xs font-semibold text-slate-700 mb-1">Ad Template</p>
      <p className="text-[11px] text-slate-500 mb-2">
        Ground generation in a layout template — or let AI choose automatically.
      </p>

      {/* Mode selector row */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={selectNone}
          className={clsx(
            'flex-1 px-3 py-1.5 rounded-lg border text-[11px] font-semibold transition',
            !state.useTemplateGrounding
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 text-slate-500 hover:border-slate-300',
          )}
        >
          None
        </button>
        <button
          type="button"
          onClick={selectAiMode}
          className={clsx(
            'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-semibold transition',
            state.useTemplateGrounding && state.pinnedTemplateIds.length === 0
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-slate-200 text-slate-500 hover:border-slate-300',
          )}
        >
          <Sparkles size={11} />
          AI Selects Best
        </button>
      </div>

      {/* Recommendation tiles */}
      {recsLoading ? (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border-2 border-slate-200 overflow-hidden animate-pulse">
              <div className="w-full aspect-[4/3] bg-slate-100" />
              <div className="px-2 py-1.5 space-y-1">
                <div className="h-2.5 bg-slate-200 rounded w-3/4" />
                <div className="h-2 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : recs.length > 0 ? (
        <>
          <p className="text-[10px] text-slate-400 mb-1.5">Recommended for this brief:</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {recs.map((rec) => (
              <TemplateThumbnail
                key={rec.template.template_id}
                template={rec.template}
                selected={state.pinnedTemplateIds.includes(rec.template.template_id)}
                onSelect={() => togglePin(rec.template.template_id)}
              />
            ))}
          </div>
        </>
      ) : null}

      {/* Explore / typeahead */}
      <button
        type="button"
        onClick={() => setExploreOpen((o) => !o)}
        className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium transition mb-1"
      >
        <ChevronDown
          size={12}
          className={clsx('transition-transform', exploreOpen && 'rotate-180')}
        />
        Explore all templates
      </button>

      {exploreOpen && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search templates…"
              className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white"
            />
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
            {searchLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={14} className="animate-spin text-slate-400" />
              </div>
            )}
            {!searchLoading && searchQuery && searchResults.length === 0 && (
              <p className="text-[11px] text-slate-400 text-center py-4">No templates found.</p>
            )}
            {!searchLoading && !searchQuery && (
              <p className="text-[11px] text-slate-400 text-center py-4">
                Type to search the template library.
              </p>
            )}
            {searchResults.map((t) => {
              const pinned = state.pinnedTemplateIds.includes(t.template_id);
              return (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => togglePin(t.template_id)}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-left transition hover:bg-slate-50',
                    pinned && 'bg-teal-50',
                  )}
                >
                  {/* Mini thumbnail */}
                  <div className="w-8 h-8 rounded-md overflow-hidden bg-slate-100 shrink-0">
                    {t.hero_ad_image_url ? (
                      <img
                        src={t.hero_ad_image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={12} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-slate-800 truncate">
                      {t.template_name}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {t.category_scope || t.funnel_stage}
                    </p>
                  </div>
                  {pinned && <Check size={12} className="text-teal-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Framing selector (shown when template grounding is on) */}
      {state.useTemplateGrounding && (
        <div className="mt-3">
          <p className="text-[10px] text-slate-500 mb-1.5 font-medium">Template influence</p>
          <div className="flex gap-1.5">
            {(
              [
                { value: 'composition_match', label: 'Composition' },
                { value: 'strict_template', label: 'Strict' },
                { value: 'style_match', label: 'Style only' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ exemplarFraming: opt.value })}
                className={clsx(
                  'px-2.5 py-1 rounded-lg border text-[10px] font-semibold transition',
                  state.exemplarFraming === opt.value
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-slate-200 text-slate-400 hover:border-slate-300',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FormatStep({
  state,
  onChange,
  brief,
  selectedAngle,
}: {
  state: WizardState;
  onChange: (u: Partial<WizardState>) => void;
  brief: GenerationBrief;
  selectedAngle: BriefAngle | null;
}) {
  const toggle = <T extends string>(arr: T[], val: T): T[] =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  return (
    <div className="space-y-5">
      {/* Ad Template */}
      <TemplatePickerSection
        state={state}
        onChange={onChange}
        brief={brief}
        selectedAngle={selectedAngle}
      />

      {/* Channels */}
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-1">Channels</p>
        <div className="grid grid-cols-2 gap-2">
          {CHANNEL_OPTIONS.map((opt) => (
            <ToggleChip
              key={opt.value}
              label={opt.label}
              sub={opt.sub}
              selected={state.channels.includes(opt.value)}
              onClick={() => onChange({ channels: toggle(state.channels, opt.value) })}
            />
          ))}
        </div>
      </div>

      {/* Sizes */}
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-1">Ad Sizes</p>
        <div className="grid grid-cols-2 gap-2">
          {SIZE_OPTIONS.map((opt) => (
            <ToggleChip
              key={opt.value}
              label={opt.label}
              sub={opt.sub}
              selected={state.sizes.includes(opt.value)}
              onClick={() => onChange({ sizes: toggle(state.sizes, opt.value) })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Wizard Step: Options ──────────────────────────────────────────────── */

function OptionsStep({
  state,
  onChange,
  selectedAngle,
}: {
  state: WizardState;
  onChange: (u: Partial<WizardState>) => void;
  selectedAngle: BriefAngle | null;
}) {
  return (
    <div className="space-y-5">
      {selectedAngle && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3">
          <p className="text-[10px] font-bold text-teal-600 uppercase tracking-wide mb-0.5">
            Angle
          </p>
          <p className="text-sm font-semibold text-slate-800">{selectedAngle.hook}</p>
        </div>
      )}

      {/* Ad count */}
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-1.5">Target ad count</p>
        <div className="grid grid-cols-4 gap-2">
          {[3, 6, 9, 12].map((n) => (
            <button
              key={n}
              onClick={() => onChange({ totalAds: n })}
              className={clsx(
                'py-2 rounded-lg border text-sm font-semibold transition',
                state.totalAds === n
                  ? 'border-teal-400 bg-teal-50 text-teal-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">
          Spread across selected angles and persona segments.
        </p>
      </div>

      {/* Toggles */}
      <div className="space-y-3 pt-1 border-t border-slate-100">
        {([
          {
            key: 'fastMode' as const,
            label: 'Fast Mode',
            desc: 'Fewer refinement iterations — faster output',
          },
          {
            key: 'enableRefinement' as const,
            label: 'Enable Refinement',
            desc: 'Post-review quality refinement loop',
          },
          {
            key: 'generateLandingPages' as const,
            label: 'Generate Landing Pages',
            desc: 'Create a landing page brief for each ad',
          },
        ] as const).map(({ key, label, desc }) => (
          <label key={key} className="flex items-center justify-between cursor-pointer gap-3">
            <div>
              <p className="text-xs font-semibold text-slate-700">{label}</p>
              <p className="text-[10px] text-slate-400">{desc}</p>
            </div>
            <button
              role="switch"
              aria-checked={state[key]}
              onClick={() => onChange({ [key]: !state[key] })}
              className={clsx(
                'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none',
                state[key] ? 'bg-teal-500' : 'bg-slate-200',
              )}
            >
              <span
                className={clsx(
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                  state[key] ? 'translate-x-4' : 'translate-x-0',
                )}
              />
            </button>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ─── Wizard Step: Confirm ──────────────────────────────────────────────── */

function ConfirmStep({
  state,
  brief,
  selectedAngle,
}: {
  state: WizardState;
  brief: GenerationBrief;
  selectedAngle: BriefAngle | null;
}) {
  const resolvedProducts = brief.product_config?.resolved_product_ids?.length;
  const strategyLabel = state.creativeStrategies.includes('auto')
    ? 'Auto'
    : state.creativeStrategies
        .map((v) => STRATEGY_OPTIONS.find((o) => o.value === v)?.label ?? v)
        .join(', ');

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-50 to-teal-50 border border-slate-200 rounded-xl p-4 space-y-2.5">
        <p className="text-sm font-bold text-slate-800">Generation Summary</p>

        <SummaryRow
          icon={<Sparkles size={12} />}
          label="Angle"
          value={
            state.selectedAngleId === '__any__'
              ? 'All approved angles'
              : (selectedAngle?.hook ?? 'Not selected')
          }
        />
        <SummaryRow
          icon={<Layers size={12} />}
          label="Strategy"
          value={strategyLabel}
        />
        {state.useTemplateGrounding && (
          <SummaryRow
            icon={<ImageIcon size={12} />}
            label="Template"
            value={
              state.pinnedTemplateIds.length
                ? `${state.pinnedTemplateIds.length} pinned · ${state.exemplarFraming.replace('_', ' ')}`
                : `AI selects best · ${state.exemplarFraming.replace('_', ' ')}`
            }
          />
        )}
        {state.channels.length > 0 && (
          <SummaryRow
            icon={<Zap size={12} />}
            label="Channels"
            value={state.channels.join(', ')}
          />
        )}
        {state.sizes.length > 0 && (
          <SummaryRow
            icon={<ImageIcon size={12} />}
            label="Sizes"
            value={state.sizes.join(', ')}
          />
        )}
        {resolvedProducts != null && (
          <SummaryRow
            icon={<Layers size={12} />}
            label="Products"
            value={`${resolvedProducts} curated`}
          />
        )}
        <SummaryRow
          icon={<ImageIcon size={12} />}
          label="Target ads"
          value={`${state.totalAds} ads`}
        />
        <SummaryRow
          icon={<Clock size={12} />}
          label="Est. time"
          value={`~${Math.ceil(state.totalAds * (state.enableRefinement ? 1.5 : 0.8))} min`}
        />
        {state.fastMode && (
          <SummaryRow icon={<Zap size={12} />} label="Fast mode" value="Enabled" />
        )}
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Generation will be queued immediately. Track progress in this Assets tab — you can
        continue working while it runs or queue more packs in parallel.
      </p>
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-slate-400">{icon}</span>
      <span className="text-xs font-semibold text-slate-600 w-20 shrink-0">{label}</span>
      <span className="text-xs text-slate-800 font-medium truncate">{value}</span>
    </div>
  );
}
